import { type FC } from "react";
import { ArrowLeft, FolderSync, MoreHorizontal, Package, Pencil, Trash2 } from "lucide-react";

import { cn } from "@/shared/utils/core";
import { formatCurrency, formatDate, formatPersonDateForDisplay } from "@/shared/utils/formatters";
import { getInstitutionLogo } from "@/shared/utils/institutionLogos";
import { getCategoryStyle } from "@/shared/utils/categoryColors";
import { CATEGORY_ICONS } from "@/shared/constants/estateCategories";
import { getEstimateRangeLabel, isEstimatedItem } from "@/shared/utils/balanceEstimates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

import type { EstateItem } from "@/shared/types/estateTypes";
import type { CategoryDetails } from "./categoryDetailFields";

interface SheetHeaderProps {
  item: EstateItem;
  details: CategoryDetails | null;
  dateOfDeath?: string | null;
  onClose: () => void;
  onRename: () => void;
  onChangeType: () => void;
  onDelete: () => void;
}

export const SheetHeader: FC<SheetHeaderProps> = ({
  item,
  details,
  dateOfDeath,
  onClose,
  onRename,
  onChangeType,
  onDelete,
}) => {
  const hardcodedLogo = getInstitutionLogo(item.item_name || "");
  const logoSrc =
    hardcodedLogo ||
    (item.plaid_institution_logo ? `data:image/png;base64,${item.plaid_institution_logo}` : null);
  const Icon = CATEGORY_ICONS[item.category as keyof typeof CATEGORY_ICONS] || Package;
  const style = getCategoryStyle(item.category);

  const asOfDate = item.last_updated_date
    ? formatPersonDateForDisplay(item.last_updated_date)
    : dateOfDeath
      ? formatPersonDateForDisplay(dateOfDeath)
      : item.plaid_last_refreshed_at
        ? formatDate(item.plaid_last_refreshed_at)
        : null;

  const displayValue =
    isEstimatedItem(item) && getEstimateRangeLabel(item)
      ? `~${getEstimateRangeLabel(item)}`
      : formatCurrency(item.balance);

  const valueCaption = (
    <>
      {details?.valueLabel}
      {asOfDate && <> as of {asOfDate}</>}
    </>
  );

  return (
    <div className="flex flex-col px-4 py-4 sm:px-6 sm:py-6">
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="shrink-0 rounded-md p-1 text-tertiary transition-colors hover:bg-subtle hover:text-primary"
          aria-label="Close panel"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        {logoSrc ? (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-primary bg-white">
            <img
              src={logoSrc}
              alt={`${item.item_name || ""} logo`}
              className="h-8 w-8 object-contain"
            />
          </div>
        ) : (
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
              style.bgClass,
            )}
          >
            <Icon className={cn("h-5 w-5", style.textClass)} />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <p className="truncate font-semibold">{item.item_name}</p>
          {(details?.subtitle || item.category) && (
            <p className="truncate text-sm text-secondary">{details?.subtitle || item.category}</p>
          )}
        </div>

        <div className="hidden shrink-0 text-right sm:block">
          <h4 className="text-xl font-bold">{displayValue}</h4>
          <p className="text-sm text-secondary">{valueCaption}</p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="shrink-0 rounded-lg border border-primary p-2 text-tertiary transition-colors hover:bg-subtle hover:text-primary"
              aria-label="Item options"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-lg border border-primary">
            <DropdownMenuItem className="gap-2" onClick={onRename}>
              <Pencil className="h-4 w-4" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={onChangeType}>
              <FolderSync className="h-4 w-4" />
              Change type
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-error" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
              Remove from Estate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-4 sm:hidden">
        <h4>{displayValue}</h4>
        <p className="text-2xs text-secondary">{valueCaption}</p>
      </div>
    </div>
  );
};
